import { defineStore } from "pinia";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";
import { TrustedNetworkAction, TrustedNetworkType } from "@/constants/network.ts";
import { useFiltersStore } from "@/_store/filters.module.ts";
import type { Pagination } from "@/types.ts";
import { type ISelection, useSelectorStore } from "@/_store/selector.module.ts";
import { getSelectionAsStringsArray } from "@/_helpers/selection";
import type { AxiosError } from "axios";
import type { ErrorPayload } from "@/_store/errors.module.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";

export const DEFAULT_MFA_FREQUENCY_HOURS = 12;

export interface NetworkModuleSettingsState {
  settings: NetworkModuleSettings;
  loading: boolean;
  // Trusted networks functionality
  trustedNetworksList: TrustedNetworkItem[];
  trustedNetworksTotal: number;
  trustedNetworksLoading: boolean;
  trustedNetworksPagination: Pagination;
}

export interface NetworkModuleSettings {
  mfaLabels: DeviceLabel[];
  mfaFrequency: number;
  trustedNetworksLabels: DeviceLabel[];
}

export interface TrustedNetworkItem {
  id: string;
  value: string;
  dateAdded: number;
  type: TrustedNetworkType;
}

const defaultNetworkModuleSettingsState: NetworkModuleSettingsState = {
  loading: false,
  settings: {
    mfaLabels: [],
    mfaFrequency: DEFAULT_MFA_FREQUENCY_HOURS,
    trustedNetworksLabels: [],
  },
  // Trusted networks defaults
  trustedNetworksList: [],
  trustedNetworksTotal: 0,
  trustedNetworksLoading: false,
  trustedNetworksPagination: {
    page: 0,
    pageSize: 25,
  },
};

export const useNetworkModuleSettingsStore = defineStore(StoreModule.NETWORK_MODULE_SETTINGS, {
  state: () => ({ ...defaultNetworkModuleSettingsState }),
  actions: {
    // Network module settings actions
    async initNetworkSettings() {
      this.loading = true;
      await this.fetchSettings();
      this.loading = false;
    },

    async fetchSettings() {
      try {
        const { data } = await axiosInstance.request({
          ...api.networkModuleSettings(),
          method: "GET",
        });
        this.settings = data;
      } catch (e) {
        console.error(e);
      }
    },

    async updateSettings(data: NetworkModuleSettings) {
      try {
        await axiosInstance.request({
          ...api.networkModuleSettings(),
          method: "PUT",
          data,
        });

        this.settings = { ...data };
      } catch (e) {
        console.error(e);
      }
    },

    // Trusted networks actions
    setPagination(pagination: Pagination) {
      this.trustedNetworksPagination = pagination;
    },

    resetPagination() {
      this.trustedNetworksPagination = {
        page: 0,
        pageSize: 25,
      };
    },

    async getTrustedNetworksList() {
      try {
        const filters = useFiltersStore().filters.trustedNetworksFilters;
        this.trustedNetworksLoading = true;
        const { data } = await axiosInstance.request({
          ...api.trustedNetworks,
          params: {
            ...this.trustedNetworksPagination,
            ...filters,
          },
        });
        this.trustedNetworksList = data.items;
        this.trustedNetworksTotal = data.total;
      } catch (e) {
        console.error(e);
      } finally {
        this.trustedNetworksLoading = false;
      }
    },

    async updateTrustedNetwork({ item }: { item: TrustedNetworkItem }) {
      try {
        await axiosInstance.request({
          ...api.trustedNetworks,
          method: "PUT",
          data: item,
        });
        await this.getTrustedNetworksList();
        useSelectorStore().deselectAllPages();
      } catch (e) {
        console.error(e);
        const error = e as AxiosError<{ errors: ErrorPayload[] }>;
        if (error.response?.data?.errors?.length) {
          throw error.response.data.errors;
        }
      }
    },

    async addTrustedNetwork({ item }: { item: TrustedNetworkItem }) {
      try {
        await axiosInstance.request({
          ...api.trustedNetworks,
          method: "POST",
          data: item,
        });
        await this.getTrustedNetworksList();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(`snackbar.messages.trustedNetworks.${TrustedNetworkAction.ADD}`, {
            type: i18n.global.t(`network.settings.trustedNetworkTypes.${item.type}`),
          })
        );
        useSelectorStore().deselectAllPages();
      } catch (e) {
        console.error(e);
        const error = e as AxiosError<{ errors: ErrorPayload[] }>;
        if (error.response?.data?.errors?.length) {
          throw error.response.data.errors;
        }
      }
    },

    async applyTrustedNetworkAction(payload: { action: string; selection: ISelection }) {
      try {
        await axiosInstance.request({
          ...api.trustedNetworkAction,
          method: "POST",
          data: {
            action: payload.action,
            selection: getSelectionAsStringsArray(payload.selection, "id"),
          },
        });
        await this.getTrustedNetworksList();
      } catch (e) {
        console.error(e);
      }
    },

    async completeEmailVerification(payload: string) {
      const request = {
        ...api.completeEmailVerificationMfa(payload),
      };

      try {
        return axiosInstance.request(request);
      } catch (error) {
        console.error(error);

        throw error;
      }
    },
  },
});
